import { Box, CircularProgress, Slide } from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useRef, useState } from "react";
import capture, { setPersonProperties } from "../../../capture";
import { useWhitelabel } from "../../../context/WhitelabelContext";
import { hostname, uniqueId, withProtocol } from "../../../utils";
import { createCompany } from "../../../utils/companyCreation";
import { useEmployeeTerminology } from "../../../utils/terminology";
import GuidedLayout from "./GuidedLayout";
import AgentsInActionStep from "./steps/AgentsInActionStep";
import BusinessIntakeStep from "./steps/BusinessIntakeStep";
import ContentPlanPreviewStep from "./steps/ContentPlanPreviewStep";
import ContentSamplesStep from "./steps/ContentSamplesStep";
import IcpPreviewStep from "./steps/IcpPreviewStep";
import LeadListPreviewStep from "./steps/LeadListPreviewStep";
import PersonalizingStep from "./steps/PersonalizingStep";
import RecapStep from "./steps/RecapStep";
import SequencePreviewStep from "./steps/SequencePreviewStep";
import TrackSelectionStep from "./steps/TrackSelectionStep";
import WebsiteWidgetsStep from "./steps/WebsiteWidgetsStep";
import { CompanyDetailField } from "./steps/CompanyDetailsPanel";
import {
  CompanyDetails,
  GuidedIntake,
  GuidedStepKey,
  IcpResult,
  OnboardingStepState,
  OnboardingTrack,
  PersonalizationState,
} from "./types";

// Company fields the AI enrichment step populates (used to flag "auto-filled").
const AI_FILLED_FIELDS: CompanyDetailField[] = [
  "description",
  "industry",
  "size",
  "location",
];

interface GuidedOnboardingProps {
  currentUser: any;
  getToken: () => Promise<string>;
  onComplete: (companyId: string) => void;
  // When set, resume this company's in-progress guided flow instead of starting fresh. The
  // user reaches this by clicking "Resume Onboarding" in the dashboard menu (we never
  // auto-resume). Pre-company screens (track/intake) aren't resumable, so a resume always
  // lands on the personalizing screen or later.
  resumeCompanyId?: string;
}

// Linear step machine for the guided flow.
//
// Personalizing is a single screen that spans both backend phases: the user reviews/confirms
// their company details (Phase 1), then — without leaving the screen — watches the ICP,
// content plan, and AI team build in place (Phase 2). The screen's layout is driven by
// `personalization.phase`/`status`, not by a step change, so it never swaps out underneath
// the user. We auto-advance to the previews once Phase 2 completes.
enum Step {
  Track = 0,
  Intake = 1,
  Personalizing = 2, // Phase 1 (confirm) + Phase 2 (build) on one screen
  IcpPreview = 3,
  CreateLeadList = 4, // Starter lead list built from the ICP
  SequencePreview = 5, // Sales sequence built from the ICP
  ContentPreview = 6,
  ContentSamples = 7, // 3 sample pieces (blog/linkedin/instagram) styled like their channels
  Agents = 8,
  WebsiteWidgets = 9, // Sales + support agents configured as website chat widgets (embed code)
  Recap = 10, // Final screen — finishing drops the user into the app.
}

// Maps each screen to its persisted progress key (GuidedStepKey) and back. We persist the
// key (not the numeric enum) so reordering/inserting steps later doesn't shift what an
// already-recorded value means. Keep this aligned with the backend ONBOARDING_UI_STEP_*.
const STEP_TO_KEY: Record<Step, GuidedStepKey> = {
  [Step.Track]: "track",
  [Step.Intake]: "intake",
  [Step.Personalizing]: "personalizing",
  [Step.IcpPreview]: "icp",
  [Step.CreateLeadList]: "list",
  [Step.SequencePreview]: "sequence",
  [Step.ContentPreview]: "content",
  [Step.ContentSamples]: "samples",
  [Step.Agents]: "agents",
  [Step.WebsiteWidgets]: "widgets",
  [Step.Recap]: "recap",
};

const KEY_TO_STEP: Record<GuidedStepKey, Step> = {
  track: Step.Track,
  intake: Step.Intake,
  personalizing: Step.Personalizing,
  icp: Step.IcpPreview,
  list: Step.CreateLeadList,
  sequence: Step.SequencePreview,
  content: Step.ContentPreview,
  samples: Step.ContentSamples,
  agents: Step.Agents,
  widgets: Step.WebsiteWidgets,
  recap: Step.Recap,
};

const POLL_INTERVAL_MS = 2500;
const MAX_POLL_ATTEMPTS = 160; // ~6.5 min ceiling so a stuck job never polls forever

// Build the initial personalization state shown the instant the personalizing screen opens,
// BEFORE the backend POST /personalize round-trip returns. This mirrors the step list the
// backend seeds in start_personalization (Phase 1 = [analyze?, details], Phase 2 = [profile,
// list, sequence, content, samples, agents, widgets]) so the full checklist — crucially the "Analyzing your website" row — is
// visible immediately and never pops in a beat later when the first poll lands.
const buildInitialPersonalization = (
  track: OnboardingTrack | null,
  website: string
): PersonalizationState => {
  const isBusiness = track === "business";
  const phase1Steps: string[] = [];
  if (isBusiness && website.trim()) phase1Steps.push("analyze");
  phase1Steps.push("details");
  const stepOrder = [...phase1Steps, "profile", "agents", "widgets", "list", "sequence", "content", "samples"];

  // First step starts generating; the rest are pending — same as the backend's initial seed.
  const steps: Record<string, OnboardingStepState> = {};
  stepOrder.forEach((key, i) => {
    steps[key] = { status: i === 0 ? "generating" : "pending" };
  });

  return {
    status: "generating",
    phase: 1,
    track: track || "business",
    steps,
    stepOrder,
    phase1Steps,
    results: {},
  };
};

const emptyIntake: GuidedIntake = {
  fullName: "",
  companyName: "",
  website: "",
  businessName: "",
  whatToStart: "",
  idealCustomers: "",
};

export default function GuidedOnboarding({
  currentUser,
  getToken,
  onComplete,
  resumeCompanyId,
}: GuidedOnboardingProps) {
  const { whitelabelSettings } = useWhitelabel();
  const { employeesLower } = useEmployeeTerminology();
  const appName = whitelabelSettings?.title || "Parallel AI";

  const [step, setStep] = useState<Step>(Step.Track);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">("left");
  const [track, setTrack] = useState<OnboardingTrack | null>(null);
  const [intake, setIntake] = useState<GuidedIntake>({
    ...emptyIntake,
    fullName: currentUser?.displayName || "",
  });

  const [createdCompanyId, setCreatedCompanyId] = useState<string>("");
  // Mirror of createdCompanyId for use inside async/fire-and-forget closures (progress
  // saves) that would otherwise capture a stale value.
  const createdCompanyIdRef = useRef<string>("");
  const [personalization, setPersonalization] =
    useState<PersonalizationState | null>(null);

  // True only while we're rehydrating a resume target, so the UI holds a spinner instead of
  // flashing the Track screen before the resume jump lands. Starts false for a fresh start
  // (no resume target), true only when we were asked to resume a specific company.
  const [resuming, setResuming] = useState(!!resumeCompanyId);

  // Editable company details shown on the personalizing screen. We seed these from the
  // user's intake, then merge in fields the backend enrichment infers — but never
  // overwrite a field the user has manually edited (tracked in `touchedFields`).
  const [companyDetails, setCompanyDetails] = useState<CompanyDetails>({});
  const touchedFields = useRef<Set<keyof CompanyDetails>>(new Set());
  const [filledByAi, setFilledByAi] = useState<Set<CompanyDetailField>>(new Set());
  const [savingDetails, setSavingDetails] = useState(false);
  // True while the recap step persists completion before we navigate into the app. We wait for
  // this save so the dashboard's company list reflects onboardingCompletedAt and doesn't keep
  // offering the "resume onboarding" entry.
  const [finishing, setFinishing] = useState(false);

  // Post-edit override for the ICP summary shown on the IcpPreview step. The actual save goes
  // through the system's ICP editor (IdealCustomerProfileDialog) inside IcpPreviewStep; when it
  // finishes it hands back a re-summarized ICP, which we hold here so the preview card reflects
  // the edit. Until then the generated summary from polling is shown.
  const [savedIcp, setSavedIcp] = useState<IcpResult | undefined>(undefined);

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollAttempts = useRef(0);

  // Set once the user edits the name field, so the async /usage backfill below never
  // clobbers what they typed if it lands late.
  const nameTouched = useRef(false);

  // True when we landed on the personalizing screen via "Resume" (not the live flow). The
  // auto-advance effect skips past a finished personalizing screen during the live flow, but
  // on resume the user explicitly wants to see that progress screen, so we suppress it once.
  const resumedToPersonalizing = useRef(false);

  const firstName = (intake.fullName || currentUser?.displayName || "")
    .trim()
    .split(" ")[0];

  // Pre-fill the user's name. `currentUser.displayName` is often null here (e.g. when the
  // session was restored from a token, or when entering onboarding from the dashboard's
  // "New Company" rather than a fresh signup), so fall back to the authoritative
  // `/usage` displayName. Only fill if the field is still empty and untouched.
  useEffect(() => {
    if (intake.fullName?.trim() || nameTouched.current) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const res = await axios.get(`${hostname}/usage`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const name = (res.data?.usage?.displayName || "").trim();
        if (cancelled || !name || nameTouched.current) return;
        setIntake((prev) =>
          prev.fullName?.trim() ? prev : { ...prev, fullName: name }
        );
      } catch (err) {
        console.error("[GuidedOnboarding] failed to load user name", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    capture("Guided Onboarding - Started", {});
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  // Once Phase 2 finishes building everything, leave the personalizing screen and show the
  // (now fully populated) previews. We only advance after Phase 2 has actually started
  // (phase >= 2) so the Phase 1 `awaiting_confirmation` rest state never trips this. On
  // `error` (a crash or poll timeout) we still advance so the user is never stuck — the
  // preview screens degrade gracefully when a result is missing.
  useEffect(() => {
    // On resume the user explicitly wants the personalizing/progress screen, so don't skip
    // past it even though generation is already complete. The live flow still auto-advances.
    if (resumedToPersonalizing.current) {
      resumedToPersonalizing.current = false;
      return;
    }
    if (
      step === Step.Personalizing &&
      (personalization?.phase || 1) >= 2 &&
      (personalization?.status === "complete" || personalization?.status === "error")
    ) {
      goNext(Step.IcpPreview);
    }
  }, [step, personalization?.phase, personalization?.status]);

  // Persist that the user finished a guided step so they can resume later. Fire-and-forget:
  // a failed save must never block navigation (the user keeps going; they'd just resume one
  // step earlier next time). No-op until the company exists, since progress lives on it —
  // the pre-company steps (track, intake) aren't resumable anyway, the company is recreated
  // from intake on the next attempt.
  const persistStepComplete = async (stepKey: GuidedStepKey) => {
    const companyId = createdCompanyIdRef.current;
    if (!companyId) return;
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/onboarding/progress`,
        { companyId, step: stepKey },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error("[GuidedOnboarding] failed to persist step progress", err);
    }
  };

  // Advancing forward marks the step we're leaving as complete (the resume point is the
  // first step NOT completed, so finishing step N records N and resumes at N+1).
  const goNext = (next: Step) => {
    void persistStepComplete(STEP_TO_KEY[step]);
    setSlideDirection("left");
    setStep(next);
  };
  const goBack = (prev: Step) => {
    setSlideDirection("right");
    setStep(prev);
  };

  // Set the created company id in both state (for render) and the ref (for closures).
  const rememberCompanyId = (id: string) => {
    createdCompanyIdRef.current = id;
    setCreatedCompanyId(id);
  };

  const updateIntake = (field: keyof GuidedIntake, value: string) => {
    // Mark the name as touched so the async /usage backfill won't overwrite the user's edit.
    if (field === "fullName") nameTouched.current = true;
    setIntake((prev) => ({ ...prev, [field]: value }));
  };

  // The user edited a company field on the personalizing screen — record it as touched so
  // polling won't overwrite it, and clear any "auto-filled" flag for that field.
  const handleDetailChange = (field: keyof CompanyDetails, value: string) => {
    touchedFields.current.add(field);
    setFilledByAi((prev) => {
      if (!prev.has(field as CompanyDetailField)) return prev;
      const next = new Set(prev);
      next.delete(field as CompanyDetailField);
      return next;
    });
    setCompanyDetails((prev) => ({ ...prev, [field]: value }));
  };

  // Phase 1 -> Phase 2 handoff. Persist the (possibly edited) company details, then tell
  // the backend to build the ICP/content/AI team from the confirmed data. We stay on the
  // same personalizing screen the whole time: flipping the local state to phase 2 collapses
  // the company-details accordion and surfaces the remaining steps as loading in place,
  // then polling drives them to completion (auto-advancing to the previews when done). We
  // reuse POST /companies (upsert by id). Save/continue failures shouldn't trap the user,
  // so we proceed regardless.
  const saveCompanyDetailsAndContinue = async () => {
    const companyId = createdCompanyId || companyDetails.id || "";
    setSavingDetails(true);
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/companies`,
        {
          id: companyId,
          name: companyDetails.name,
          website: companyDetails.website,
          description: companyDetails.description,
          industry: companyDetails.industry,
          // `type` is not a user-facing field; the company form mirrors it from industry.
          type: companyDetails.industry,
          size: companyDetails.size,
          location: companyDetails.location,
          // The auto-detected (or user-replaced) logo. Saving it here keeps a user's
          // replacement from being lost when Phase 2 starts.
          logoUrl: companyDetails.logoUrl,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      capture("Guided Onboarding - Company Details Saved", {
        editedFields: Array.from(touchedFields.current),
      });

      // Kick off Phase 2 (ICP -> content -> AI team) now that details are confirmed,
      // then resume polling so the previews populate as each step finishes.
      await axios.post(
        `${hostname}/onboarding/personalize/continue`,
        { companyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Flip to phase 2 in place — the personalizing screen reacts to this by collapsing the
      // company-details accordion and showing the ICP/content/team steps as loading. No step
      // change, so the screen never swaps out underneath the user.
      setPersonalization((prev) =>
        prev ? { ...prev, status: "generating", phase: 2 } : prev
      );
      pollAttempts.current = 0;
      if (pollTimer.current) clearTimeout(pollTimer.current);
      pollStatus(companyId);
    } catch (err) {
      console.error("[GuidedOnboarding] failed to confirm company details", err);
    } finally {
      setSavingDetails(false);
    }
  };

  // The system ICP editor (in IcpPreviewStep) finished a save and handed back a re-summarized
  // ICP — hold it so the preview card reflects the edit. Also fired as an analytics signal.
  const handleIcpSaved = (next: IcpResult) => {
    setSavedIcp(next);
    capture("Guided Onboarding - ICP Edited", {});
  };

  const handleSelectTrack = (t: OnboardingTrack) => {
    setTrack(t);
    capture("Guided Onboarding - Track Selected", { track: t });
    setPersonProperties({ onboardingTrack: t });
    goNext(Step.Intake);
  };

  // ---- Polling -----------------------------------------------------------

  const pollStatus = useCallback(
    async (companyId: string) => {
      if (pollAttempts.current >= MAX_POLL_ATTEMPTS) {
        setPersonalization((prev) =>
          prev?.status === "complete" || prev?.status === "awaiting_confirmation"
            ? prev
            : { status: "error", error: "Personalization is taking longer than expected." }
        );
        return;
      }
      pollAttempts.current += 1;

      let keepPolling = true;
      try {
        const token = await getToken();
        const res = await axios.get(`${hostname}/onboarding/personalize/status`, {
          params: { companyId },
          headers: { Authorization: `Bearer ${token}` },
        });
        const state: PersonalizationState = res.data;

        // Skip the state update (and the re-render it triggers) when nothing changed.
        setPersonalization((prev) =>
          JSON.stringify(prev) === JSON.stringify(state) ? prev : state
        );

        // Merge backend-inferred company fields into the editable panel, skipping any
        // field the user has already touched so we never clobber their edits.
        if (state.company) {
          const incoming = state.company;
          setCompanyDetails((prev) => {
            const next = { ...prev };
            const newlyFilled: CompanyDetailField[] = [];
            let changed = false;
            (Object.keys(incoming) as (keyof CompanyDetails)[]).forEach((field) => {
              const value = (incoming[field] || "").trim();
              if (!value || touchedFields.current.has(field)) return;
              if ((prev[field] || "") === value) return;
              next[field] = value;
              changed = true;
              if (AI_FILLED_FIELDS.includes(field as CompanyDetailField)) {
                newlyFilled.push(field as CompanyDetailField);
              }
            });
            if (newlyFilled.length) {
              setFilledByAi((prevSet) => new Set([...prevSet, ...newlyFilled]));
            }
            // Return prev when nothing changed so polling doesn't force a re-render every tick.
            return changed ? next : prev;
          });
        }

        // Stop polling at any resting state. `awaiting_confirmation` (Phase 1 done) is a
        // resting state too — polling resumes when the user confirms and Phase 2 starts.
        if (state.status !== "generating") {
          keepPolling = false;
          if (state.status === "complete") {
            capture("Guided Onboarding - Personalization Complete", {
              hadErrors: !!state.hadErrors,
            });
          }
        }
      } catch (err) {
        // Transient failures shouldn't strand the user; the attempt cap bounds retries.
        console.error("[GuidedOnboarding] poll failed", err);
      }

      if (keepPolling) {
        pollTimer.current = setTimeout(() => pollStatus(companyId), POLL_INTERVAL_MS);
      }
    },
    [getToken]
  );

  // ---- Resume an in-progress flow ----------------------------------------

  // When the user clicked "Resume Onboarding" (resumeCompanyId is set), rehydrate that
  // company's flow and drop them back where they left off. We never auto-resume — without a
  // resume target this effect does nothing and the flow starts fresh at the Track screen.
  // Best-effort: any failure falls back to a fresh start.
  useEffect(() => {
    if (!resumeCompanyId) return;
    let cancelled = false;
    const resume = async () => {
      try {
        const token = await getToken();
        // Pull the full personalization status so the preview screens have their generated
        // results, and use its `progress.resumeStep` (the step right after the furthest one
        // the user completed) as the jump target.
        const statusRes = await axios.get(
          `${hostname}/onboarding/personalize/status`,
          {
            params: { companyId: resumeCompanyId },
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (cancelled) return;
        const state: PersonalizationState = statusRes.data;

        const resumeKey: GuidedStepKey | undefined =
          state.progress?.resumeStep || undefined;
        // Resume only ever lands on the personalizing screen or later (pre-company screens
        // aren't resumable). If we can't resolve a valid target, fall back to a fresh start.
        const targetStep =
          resumeKey && KEY_TO_STEP[resumeKey] !== undefined
            ? KEY_TO_STEP[resumeKey]
            : undefined;
        if (targetStep === undefined || targetStep <= Step.Intake) return;

        rememberCompanyId(resumeCompanyId);
        if (state.track) setTrack(state.track);
        setPersonalization(state);
        if (state.company) {
          setCompanyDetails((prev) => ({ ...prev, ...state.company }));
        }
        capture("Guided Onboarding - Resumed", { step: resumeKey });

        if (targetStep === Step.Personalizing) {
          // Land on the progress screen and keep the user there (don't auto-skip a finished
          // build) — they came back specifically to see/continue it.
          resumedToPersonalizing.current = true;
          // If generation was still running when they left, re-attach the poll loop so the
          // remaining steps finish. A resting state (awaiting_confirmation / complete / error)
          // needs no polling.
          if (state.status === "generating") {
            pollAttempts.current = 0;
            if (pollTimer.current) clearTimeout(pollTimer.current);
            pollStatus(resumeCompanyId);
          }
        }
        setStep(targetStep);
      } catch (err) {
        // Couldn't load resume state — just start fresh from the Track screen.
        console.error("[GuidedOnboarding] resume failed", err);
      } finally {
        if (!cancelled) setResuming(false);
      }
    };
    resume();
    return () => {
      cancelled = true;
    };
    // Run once on mount for the given resume target.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Company creation + personalization kickoff ------------------------

  const buildCompanyData = (website: string) => {
    const isBusiness = track === "business";
    return {
      uid: "", // set by backend from JWT
      id: uniqueId(),
      name: isBusiness ? intake.companyName : intake.businessName,
      industry: "",
      website: isBusiness ? website : "",
      size: "",
      location: "",
      // Business track leaves description blank — the backend fills it by analyzing the site.
      description: isBusiness ? "" : intake.whatToStart,
      type: "",
      logoUrl: "",
      employees: [] as any[],
    };
  };

  const startPersonalization = async (companyId: string, website: string) => {
    const token = await getToken();
    await axios.post(
      `${hostname}/onboarding/personalize`,
      {
        companyId,
        track,
        intake: {
          fullName: intake.fullName,
          companyName: intake.companyName,
          website,
          // Business track has no typed description — the backend derives it from the site.
          description: track === "business" ? "" : intake.whatToStart,
          idealCustomers: intake.idealCustomers,
        },
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
  };

  // Persist the user's display name in the background — nothing downstream depends on it,
  // so we don't await it (it must not delay the screen transition or company creation).
  const saveProfileName = async () => {
    if (!intake.fullName?.trim()) return;
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/users/profile`,
        { displayName: intake.fullName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error("[GuidedOnboarding] failed to save profile name", err);
    }
  };

  const handleIntakeNext = async () => {
    void saveProfileName();

    capture("Guided Onboarding - Intake Submitted", { track });

    // Normalize the website here (not via the intake setState in BusinessIntakeStep): we
    // navigate away in the same tick, so the setState-updated `intake.website` would still be
    // stale when the code below reads it — the un-prefixed URL would then be sent to the
    // backend's site reader. Thread the corrected value through everything that consumes it.
    const website = track === "business" ? withProtocol(intake.website) : "";
    if (website && website !== intake.website) updateIntake("website", website);

    // Move to the personalizing screen immediately; create + kick off in the background.
    // Seed the FULL step list now (mirroring what the backend will return) so the checklist —
    // including "Analyzing your website" — renders immediately rather than popping in once the
    // first poll lands.
    goNext(Step.Personalizing);
    setPersonalization(buildInitialPersonalization(track, website));

    const companyData = buildCompanyData(website);

    // Seed the editable details panel with what the user typed; enrichment fills the rest.
    touchedFields.current = new Set();
    setFilledByAi(new Set());
    setCompanyDetails({
      id: companyData.id,
      name: companyData.name,
      website: companyData.website,
      description: companyData.description,
      industry: "",
      type: "",
      size: "",
      location: "",
      logoUrl: "",
    });

    try {
      // Create the company (no employees yet — personalization suggests the team).
      const result = await createCompany({
        getToken,
        companyData,
        createEmployees: false,
      });
      if (!result.success) throw new Error(result.error);

      rememberCompanyId(companyData.id);
      // Record `intake` as soon as the company exists so an abandon during personalizing is
      // still resumable (resumeStep becomes `personalizing`, landing the user back here).
      // Without this, a company created but left mid-personalizing would never show the
      // resume button. Fire-and-forget — must not delay personalization kickoff.
      void persistStepComplete(STEP_TO_KEY[Step.Intake]);
      await startPersonalization(companyData.id, website);
      pollStatus(companyData.id);
    } catch (err) {
      console.error("[GuidedOnboarding] company creation / personalization failed", err);
      setPersonalization({ status: "error", error: "Setup failed" });
    }
  };

  // ---- Completion --------------------------------------------------------

  // The recap is the last screen — finishing drops the user straight into the app (no
  // separate "all set up" or subscribe screens). Persist the recap step too: it stamps
  // onboardingCompletedAt on the company, which marks the flow finished so it won't offer
  // to resume next time. Unlike the other steps, we AWAIT this save before navigating: the
  // dashboard reloads its company list on mount, so if we navigate before onboardingCompletedAt
  // is stamped, the company still matches the "resume onboarding" filter and the "x of y"
  // entry lingers. persistStepComplete swallows its own errors, so a failed save still lets
  // the user into the app (they'd just be re-offered resume, landing on the recap again).
  const finishOnboarding = async () => {
    if (finishing) return;
    setFinishing(true);
    await persistStepComplete(STEP_TO_KEY[Step.Recap]);
    capture("Guided Onboarding - Completed", {
      track,
      hadErrors: !!personalization?.hadErrors,
    });
    setPersonProperties({ onboardingCompleted: true });
    onComplete(createdCompanyId);
  };

  // ---- Render ------------------------------------------------------------

  const results = personalization?.results || {};

  const renderStep = () => {
    switch (step) {
      case Step.Track:
        return (
          <TrackSelectionStep
            appName={appName}
            selectedTrack={track}
            onSelect={handleSelectTrack}
          />
        );
      case Step.Intake:
        return (
          <BusinessIntakeStep
            track={track || "business"}
            intake={intake}
            updateIntake={updateIntake}
            onNext={handleIntakeNext}
            onBack={() => goBack(Step.Track)}
          />
        );
      case Step.Personalizing:
        return (
          <PersonalizingStep
            appName={appName}
            firstName={firstName}
            state={personalization}
            details={companyDetails}
            touched={touchedFields.current}
            filledByAi={filledByAi}
            onDetailChange={handleDetailChange}
            onContinue={saveCompanyDetailsAndContinue}
            // Forward path when the user resumed onto an already-finished build (no
            // auto-advance happened). Records the personalizing step and moves to previews.
            onProceed={() => goNext(Step.IcpPreview)}
            saving={savingDetails}
            getToken={getToken}
          />
        );
      case Step.IcpPreview:
        return (
          <IcpPreviewStep
            icp={savedIcp ?? results.icp}
            companyId={createdCompanyId || companyDetails.id || ""}
            getToken={getToken}
            onIcpSaved={handleIcpSaved}
            onNext={() => goNext(Step.CreateLeadList)}
            // Skip back to Intake rather than the personalizing screen: by now Phase 2 is
            // complete, so that screen would just auto-advance straight back here.
            onBack={() => goBack(Step.Intake)}
          />
        );
      case Step.CreateLeadList:
        return (
          <LeadListPreviewStep
            list={results.list}
            onNext={() => goNext(Step.SequencePreview)}
            onBack={() => goBack(Step.IcpPreview)}
          />
        );
      case Step.SequencePreview:
        return (
          <SequencePreviewStep
            sequence={results.sequence}
            companyId={createdCompanyId || companyDetails.id || ""}
            // Minimal shell for InstallIntegrationDialog; it only reads company.uid (Slack
            // install, unused for email). currentUser.uid is the authenticated user's id.
            company={
              {
                uid: currentUser?.uid || "",
                id: createdCompanyId || companyDetails.id || "",
                name: companyDetails.name || "",
              } as any
            }
            getToken={getToken}
            onNext={() => goNext(Step.ContentPreview)}
            onBack={() => goBack(Step.CreateLeadList)}
          />
        );
      case Step.ContentPreview:
        return (
          <ContentPlanPreviewStep
            content={results.content}
            onNext={() => goNext(Step.ContentSamples)}
            onBack={() => goBack(Step.SequencePreview)}
          />
        );
      case Step.ContentSamples:
        return (
          <ContentSamplesStep
            samples={results.samples}
            onNext={() => goNext(Step.Agents)}
            onBack={() => goBack(Step.ContentPreview)}
          />
        );
      case Step.Agents:
        return (
          <AgentsInActionStep
            agents={results.agents}
            employeesLower={employeesLower}
            onNext={() => goNext(Step.WebsiteWidgets)}
            onBack={() => goBack(Step.ContentSamples)}
          />
        );
      case Step.WebsiteWidgets:
        return (
          <WebsiteWidgetsStep
            widgets={results.widgets}
            onNext={() => goNext(Step.Recap)}
            onBack={() => goBack(Step.Agents)}
          />
        );
      case Step.Recap:
        return (
          <RecapStep
            appName={appName}
            companyName={companyDetails.name || ""}
            hasIcp={!!results.icp}
            hasLeadList={!!results.list?.leadCount}
            hasSequence={!!results.sequence?.steps?.length}
            hasContent={!!results.content?.pillars?.length}
            hasSamples={!!results.samples?.pieces?.length}
            hasAgents={!!results.agents?.length}
            hasWidgets={!!results.widgets?.agents?.length}
            onNext={finishOnboarding}
            onBack={() => goBack(Step.WebsiteWidgets)}
            finishing={finishing}
          />
        );
      default:
        return null;
    }
  };

  const hero = getHero(step, appName);
  const wide = step >= Step.IcpPreview && step <= Step.Recap;

  // While we check for an in-progress flow to resume, hold a spinner inside the layout shell
  // rather than flashing the Track screen and then jumping the user forward a beat later.
  if (resuming) {
    return (
      <GuidedLayout heroTitle={hero.title} heroSubtitle={hero.subtitle} maxWidth={wide ? 640 : 560}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      </GuidedLayout>
    );
  }

  return (
    <GuidedLayout heroTitle={hero.title} heroSubtitle={hero.subtitle} maxWidth={wide ? 640 : 560}>
      <Slide direction={slideDirection} in mountOnEnter unmountOnExit key={step}>
        <div>{renderStep()}</div>
      </Slide>
    </GuidedLayout>
  );
}

function getHero(step: Step, appName: string): { title: string; subtitle: string } {
  switch (step) {
    case Step.Track:
      return {
        title: "Welcome to the Future of Work",
        subtitle: `Build your AI workforce with ${appName} in minutes`,
      };
    case Step.Intake:
      return {
        title: "Tell Us About Your Business",
        subtitle: "We'll personalize everything to match your unique goals",
      };
    case Step.Personalizing:
      return {
        title: "Personalizing Your Workspace",
        subtitle: "Review the details we found, then we'll build the rest for you",
      };
    case Step.IcpPreview:
    case Step.CreateLeadList:
    case Step.SequencePreview:
    case Step.ContentPreview:
    case Step.ContentSamples:
    case Step.Agents:
    case Step.WebsiteWidgets:
      return {
        title: "Built For You",
        subtitle: "Everything below was generated from your business",
      };
    case Step.Recap:
      return {
        title: "Ready When You Are",
        subtitle: "Put your AI team to work and scale faster",
      };
    default:
      return {
        title: "You're All Set! 🎉",
        subtitle: "Let's start building something amazing together",
      };
  }
}
